import { create } from "zustand";

import {
  getAccount,
  watchAccount,
  type GetAccountReturnType,
  type Config as WagmiConfig,
} from "@wagmi/core";

import type * as IOG from "@bracket/in-or-out-server";
import { getWSURL } from "./helpers";

const showLogs = window.location.search.includes("feature=in-out");

type TableRecentWinsData = {
  id: string;
  choice: IOG.Choice;
  time: IOG.UnixEpochTimeStamp;
  total_bars: number;
}[];

type TablePlayerPlaysData = {
  choice: IOG.Choice;
  time: IOG.UnixEpochTimeStamp;
  isWinning: boolean;
  total_bars: number;
}[];

export declare type InOrOutStore = {
  initialized: boolean;
  initializing: boolean;
  connected: boolean;
  connectionId: string;
  wagmiConfig: WagmiConfig;
  socket: WebSocket | null;
  history: IOG.PriceData[];

  previousResult: IOG.PriceData | null;
  currentResult: IOG.PriceData | null;
  currentInOutRange: number | null;

  barsWonToday: number;
  barsWonTotal: number;
  tableRecentBars: TableRecentWinsData;
  tableYourDailyPlays: TablePlayerPlaysData;
  activeGameState: Omit<IOG.ActiveGameState, "address"> | null;
  hasStaked: boolean;
  user_stake: bigint;

  initialize: (config: WagmiConfig, props?: { user_stake?:bigint; }) => Promise<void>;
  join: (address: string) => Promise<void>;
  playStart: (choice: "IN" | "OUT") => void;
  resetPlay: () => void;
  _joinGameServer: () => Promise<void>;
  onJoinedGame: (event: IOG.JoinEvent) => void;
  onNewResult: (event: IOG.PriceDataEvent) => void;
  onWebSocketEvent: (event: MessageEvent) => Promise<void>;
  onWebsocketConnect: (event: MessageEvent) => void;
  onWebsocketError: (event: MessageEvent) => void;
  onWebsocketClose: (event: CloseEvent) => void;
  setTableRecentBars: (data: TableRecentWinsData) => void;
	updatedUserStake: (value:bigint) => void;

  resetStore: () => void;
};

const HISTORY_LIMIT = 10;

const createLoggingSocket = (socket: WebSocket): WebSocket => {
  const originalSend = socket.send;

  socket.send = (data: string) => {
    showLogs && console.log("websocket:send:event", data);
    return originalSend.call(socket, data);
  };

  socket.addEventListener("message", (event: MessageEvent) => {
    showLogs && console.log("websocket:receive:event", event.data);
  });

  return socket;
};

export const useInOrOutStore = create<InOrOutStore>()(
  // persist(
  (set, get) => ({
    initialized: false,
    initializing: false,
    connected: false,
    connectionId: null,
    wagmiConfig: null,
    socket: null,
    // _onAccountChangeLsn: null,
    history: [],
    previousResult: null,
    currentResult: null,
    currentInOutRange: null,
    barsWonToday: 0,
    barsWonTotal: 0,
    tableRecentBars: [],
    tableYourDailyPlays: [],
    activeGameState: null,
    hasStaked: null,
    user_stake: BigInt(0),

    initialize: async (config, props: { user_stake?: bigint } = {}) => {
      set({
        initializing: true,
        initialized: false,
      });
      set({
				user_stake: props.user_stake || BigInt(0),
			});
      await get()._joinGameServer();

      // if( status === "connected" ) {

      //initialization code here.
      set({ initializing: false, initialized: true });
      return;

      // }
      throw new Error(
        "InOrOutStore cannot initialized in any web3 state other than connected."
      );
    },

    onWebSocketEvent: async ({ data }) => {
      const message = JSON.parse(data) as IOG.InOutEvent;

      switch (message.event) {
        case "join":
          return get().onJoinedGame(message as IOG.JoinEvent);
        case "play:price:data":
          return get().onNewResult(message as IOG.PriceDataEvent);
        case "bars:won:data":
          return set({
            barsWonToday: (message as IOG.BarsWonEvent).bars_won_today,
            barsWonTotal: (message as IOG.BarsWonEvent).bars_won_total,
          });
        case "table:recent:wins":
          const newBars = (message as IOG.TablerecentBarsEvent).data.map(
            (d) => ({
              id: d.player,
              choice: d.choice,
              time: new Date(d.start_time).getTime(),
              total_bars: d.total_bars,
            })
          );

          get().setTableRecentBars(newBars);
          return;
        case "table:your:daily:plays":
          return set({
            tableYourDailyPlays: (message as IOG.TablerecentBarsEvent).data.map(
              (d) => ({
                id: d.player,
                choice: d.choice,
                time: new Date(d.start_time).getTime(),
                total_bars: d.total_bars,
                isWinning: d.result === "win",
              })
            ),
          });
        case "play:info":
          const msg = message as IOG.PlayInfoEvent;

          if (msg.result === "error") {
            console.error("play:info:error", msg);
            if (msg.error === "no_stake") {
              return set({
                hasStaked: false,
              });
            }
          } else {
            return set({
              activeGameState: {
                choice: msg.choice,
                startTime: msg.startTime,
                timeLeft: msg.timeLeft,
                result: msg.result,
                startPrice: msg.startPrice,
                gameMaxPriceDiff: msg.gameMaxPriceDiff,
                inOutRange: msg.inOutRange,
              },
              hasStaked: true,
            });
          }
      }
    },

    join: async (address: string) => {
      const { socket } = get();
      if (socket) {
        const event = JSON.stringify({ event: "join", address });
        socket.send(event);
        return;
      }
      console.error("no socket", socket);
    },

    playStart: (choice: "IN" | "OUT") => {
      try {
        const { connected, socket } = get();

        if (connected && socket) {
          const playStartEvent: IOG.PlayStartEvent = {
            event: "play:start",
            choice,
          };

          socket.send(JSON.stringify(playStartEvent));
          return;
        }
        throw new Error("not connected.");
      } catch (err) {
        throw new Error(`InOrOutStore::play has errored - ${err.message}`);
      }
    },

    resetPlay: () => {
      set({ activeGameState: null });
    },

    _joinGameServer: async () => {
      const { host, } = document.location;
      const url = [
        "stake.bracket.fi",
        "prod2-stake.bracket.fi",
        "prod1-stake.bracket.fi"
      ].includes(host)
        ? "wss://game.bracket.fi:3001/in-or-out"
        : "/game/in-or-out";

      const socket = new WebSocket(url);
      showLogs && console.log("join game server");
      const loggingSocket = createLoggingSocket(socket);

      loggingSocket.onopen = get().onWebsocketConnect;
      loggingSocket.onerror = get().onWebsocketError;
      loggingSocket.onclose = get().onWebsocketClose;
      loggingSocket.onmessage = get().onWebSocketEvent;

      set({ socket: loggingSocket });
    },

    onJoinedGame: (event) => {
      const { id, hasStaked } = event;
      const staked = hasStaked || get().user_stake > 0;
      set({ connectionId: id, hasStaked: staked, });
    },

    onNewResult: (event) => {
      const { result, inOutRange } = event;
      const { history } = get();
      let newHistory = history.concat();
      newHistory.push(result);
      newHistory = newHistory
        .filter((d) => d?.price > 0 && d?.time > 0)
        .sort((a, b) => a.time - b.time);

      let previousResult = null;
      let lastResult = null;

      if (newHistory.length >= 2) {
        lastResult = newHistory[newHistory.length - 1];
        const secondLastResult = newHistory[newHistory.length - 2];

        previousResult = get().currentResult || secondLastResult;
      }

      set({
        history: newHistory.slice(-HISTORY_LIMIT),
        previousResult: previousResult,
        currentResult: lastResult,
        currentInOutRange: inOutRange,
      });
    },

    onWebsocketError: (event) => {
      console.log("websocket error:", event);
    },

    onWebsocketClose: (event) => {
      set({
        socket: null,
        connected: false,
      });
    },

    onWebsocketConnect: (event) => {
      set({
        connected: true,
      });
    },

    resetStore: () => {
      set({
        initialized: false,
        initializing: false,
        connected: false,
        connectionId: null,
        wagmiConfig: null,
        socket: null,
        history: [],
        previousResult: null,
        currentResult: null,
        currentInOutRange: null,
        barsWonToday: 0,
        barsWonTotal: 0,
        tableRecentBars: [],
        tableYourDailyPlays: [],
        activeGameState: null,
        hasStaked: null,
      });
    },

    setTableRecentBars: (newData: TableRecentWinsData) => {
      set((state) => {
        const mergedBars = [...state.tableRecentBars, ...newData];

        // Remove duplicates based on `id` and `time`
        const uniqueBars = mergedBars.filter(
          (bar, index, self) =>
            index ===
            self.findIndex((b) => b.id === bar.id && b.time === bar.time)
        );

        const sortedBars = uniqueBars
          .sort((a, b) => b.time - a.time)
          .slice(0, 10);

        return {
          tableRecentBars: sortedBars,
        };
      });
    },

    updatedUserStake: (value) => {
      set({
        user_stake: value,
        hasStaked: value > 0,
      });
    },

  })
);
